/**
 * FilterHarvestMonitoring Component
 * Filter component for harvest monitoring
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Button,
    FormControl,
    FormLabel,
    Input,
    VStack,
    Grid,
    Select,
    useTheme,
    Flex,
    Text,
    HStack,
    Tooltip,
} from '@chakra-ui/react';
import { MdClear } from 'react-icons/md';
import { FiSearch, FiDownload } from 'react-icons/fi';
import dayjs from 'dayjs';
import api from '../../../auth/apiClient';
import type { HarvestMonitoringFilters } from '../types';

interface Company {
    id: number;
    name: string;
}

interface Estate {
    id: number;
    name: string;
    odoo_id: number;
    company_id?: number;
}

interface Division {
    id: number;
    name: string;
    estate_id: number;
    odoo_id: number;
}

interface FilterHarvestMonitoringProps {
    onFiltersChange: (filters: HarvestMonitoringFilters, mode?: 'block' | 'spb') => void;
    isLoading?: boolean;
    isDivisionRequired?: boolean;
    showActivityFilter?: boolean;
    onDownloadPDF?: () => void;
    isDownloadDisabled?: boolean;
    viewMode?: 'block' | 'spb';
    showGroupBy?: boolean;
    isDateRange?: boolean;
    onDownloadExcel?: () => void;
    isExcelDisabled?: boolean;
}

const MAX_RANGE_DAYS = 31;

// Clamp end to [start, start + 30 days] so the range stays within the API limit.
const clampEndDate = (start: string, end: string): string => {
    const maxEnd = dayjs(start).add(MAX_RANGE_DAYS - 1, 'day');
    if (dayjs(end).isBefore(start)) return start;
    if (dayjs(end).isAfter(maxEnd)) return maxEnd.format('YYYY-MM-DD');
    return end;
};

const toApiDate = (date: string): string => dayjs(date).format('YYYYMMDD');

export const FilterHarvestMonitoring: React.FC<FilterHarvestMonitoringProps> = ({
    onFiltersChange,
    isLoading = false,
    isDivisionRequired = false,
    showActivityFilter = false,
    onDownloadPDF,
    isDownloadDisabled = false,
    viewMode = 'block',
    showGroupBy = false,
    isDateRange = false,
    onDownloadExcel,
    isExcelDisabled = false,
}) => {
    const theme = useTheme();
    // Default format YYYY-MM-DD
    const today = dayjs().format('YYYY-MM-DD');
    const [currentDate, setCurrentDate] = useState<string>(today);
    const [endDate, setEndDate] = useState<string>(today);
    const [tempViewMode, setTempViewMode] = useState<'block' | 'spb'>(viewMode);

    useEffect(() => {
        setTempViewMode(viewMode);
    }, [viewMode]);

    // State for Select options
    const [companies, setCompanies] = useState<Company[]>([]);
    const [estates, setEstates] = useState<Estate[]>([]);
    const [divisions, setDivisions] = useState<Division[]>([]);
    const [allDivisions, setAllDivisions] = useState<Division[]>([]);

    const [selectedCompanyId, setSelectedCompanyId] = useState<number | undefined>(undefined);
    const [selectedEstateId, setSelectedEstateId] = useState<number | undefined>(14);
    const [selectedDivisionId, setSelectedDivisionId] = useState<number | undefined>(undefined);
    const [activityCode, setActivityCode] = useState<string>('all');

    const filteredEstates = useMemo(() => {
        if (!selectedCompanyId) return estates;
        return estates.filter((estate) => estate.company_id === selectedCompanyId);
    }, [estates, selectedCompanyId]);

    // Style constants
    const inputStyles = {
        size: 'sm' as const,
        borderRadius: 'lg',
        borderColor: 'gray.200',
        _hover: { borderColor: 'gray.300' },
        _focus: {
            borderColor: theme.colors.blue[400],
            boxShadow: `0 0 0 1px ${theme.colors.blue[400]}`,
        },
    };

    // Fetch options on component mount
    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const [optionsResponse, estatesDbResponse] = await Promise.all([
                    api.get('/inspeksi/options'),
                    api.get('/inspeksi/web/estate-db'),
                ]);

                const data = optionsResponse.data;
                const estatesDbData = estatesDbResponse.data;

                const fetchedCompanies = data.companies || [];
                setCompanies(fetchedCompanies);
                setAllDivisions(data.divisis || []);

                const mergedEstates: Estate[] = (estatesDbData || []).map((estateDb: any) => {
                    const optionsEstate = (data.estates || []).find((est: any) => est.name === estateDb.name);
                    return {
                        id: estateDb.id,
                        name: estateDb.name,
                        company_id: estateDb.company_id,
                        odoo_id: optionsEstate?.odoo_id || estateDb.id,
                    };
                });

                setEstates(mergedEstates);


                if (fetchedCompanies.length > 0) {
                    const defaultCompany = fetchedCompanies[0];
                    setSelectedCompanyId(defaultCompany.id);

                    const estate14 = mergedEstates.find(e => e.odoo_id === 14);
                    let allowedDivs: string[] | undefined = undefined;
                    let defaultEstateId: number | undefined = 14;

                    if (estate14 && estate14.company_id === defaultCompany.id) {
                        allowedDivs = (data.divisis || []).filter((d: any) => d.estate_id === estate14.id).map((d: any) => d.name);
                    } else {
                        defaultEstateId = undefined;
                        const companyEstateIds = new Set(mergedEstates.filter(e => e.company_id === defaultCompany.id).map(e => e.id));
                        allowedDivs = (data.divisis || []).filter((d: any) => companyEstateIds.has(d.estate_id)).map((d: any) => d.name);
                    }

                    const formattedDate = toApiDate(today);
                    onFiltersChange({
                        current_date: formattedDate,
                        start_date: formattedDate,
                        end_date: formattedDate,
                        company_id: defaultCompany.id,
                        estate_id: defaultEstateId,
                        allowed_divisions: allowedDivs,
                        activity_code: showActivityFilter ? 'all' : undefined
                    }, viewMode);
                }
            } catch (error) {
                console.error('Error fetching options:', error);
            }
        };
        fetchOptions();
    }, []);

    // Filter divisions when estate changes
    useEffect(() => {
        if (!selectedEstateId) {
            setDivisions([]);
            return;
        }

        const selectedEstate = estates.find(e => e.odoo_id === selectedEstateId);
        if (selectedEstate) {
            const filteredDivisions = allDivisions.filter(
                (div: Division) => div.estate_id === selectedEstate.id
            );
            setDivisions(filteredDivisions);
        }
    }, [selectedEstateId, estates, allDivisions]);

    // Auto-select estate when isDivisionRequired is true
    useEffect(() => {
        if (isDivisionRequired && filteredEstates.length > 0) {
            const currentIsValid = filteredEstates.some(e => e.odoo_id === selectedEstateId);
            if (!currentIsValid) {
                const hasEstate14 = filteredEstates.some(e => e.odoo_id === 14);
                setSelectedEstateId(hasEstate14 ? 14 : filteredEstates[0].odoo_id);
            }
        }
    }, [isDivisionRequired, filteredEstates, selectedEstateId]);


    const handleApplyFilters = () => {
        const formattedDate = dayjs(currentDate).format('YYYYMMDD');

        let allowedDivs: string[] | undefined = undefined;

        if (selectedDivisionId) {
            const division = divisions.find(d => d.odoo_id === selectedDivisionId);
            if (division) allowedDivs = [division.name];
        } else if (selectedEstateId) {
            const estate = estates.find(e => e.odoo_id === selectedEstateId);
            if (estate) {
                // Find all divisions belonging to this estate
                allowedDivs = allDivisions.filter(d => d.estate_id === estate.id).map(d => d.name);
            }
        } else if (selectedCompanyId) {
            const companyEstateIds = new Set(estates.filter(e => e.company_id === selectedCompanyId).map(e => e.id));
            allowedDivs = allDivisions.filter(d => companyEstateIds.has(d.estate_id)).map(d => d.name);
        }

        onFiltersChange({
            current_date: formattedDate,
            start_date: formattedDate,
            end_date: isDateRange ? toApiDate(endDate) : formattedDate,
            company_id: selectedCompanyId,
            estate_id: selectedEstateId,
            division_id: selectedDivisionId,
            allowed_divisions: allowedDivs,
            activity_code: showActivityFilter ? activityCode : 'all'
        }, tempViewMode);
    };

    const handleReset = () => {
        setCurrentDate(today);
        setEndDate(today);
        setActivityCode('all');

        let resetCompanyId: number | undefined = undefined;
        let allowedDivs: string[] | undefined = undefined;
        let defaultEstateId: number | undefined = 14;

        if (companies.length > 0) {
            resetCompanyId = companies[0].id;
            setSelectedCompanyId(resetCompanyId);

            const estate14 = estates.find(e => e.odoo_id === defaultEstateId);
            if (estate14 && estate14.company_id === resetCompanyId) {
                allowedDivs = allDivisions.filter(d => d.estate_id === estate14.id).map(d => d.name);
            } else {
                defaultEstateId = undefined;
                const companyEstateIds = new Set(estates.filter(e => e.company_id === resetCompanyId).map(e => e.id));
                allowedDivs = allDivisions.filter(d => companyEstateIds.has(d.estate_id)).map(d => d.name);
            }
        } else {
            setSelectedCompanyId(undefined);
            defaultEstateId = undefined;
        }

        setSelectedEstateId(defaultEstateId);
        setSelectedDivisionId(undefined);
        setTempViewMode('block');

        onFiltersChange({
            current_date: toApiDate(today),
            start_date: toApiDate(today),
            end_date: toApiDate(today),
            company_id: resetCompanyId,
            estate_id: defaultEstateId,
            division_id: undefined,
            allowed_divisions: allowedDivs,
            activity_code: showActivityFilter ? 'all' : 'all'
        }, 'block');
    };


    const handleCompanyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val = e.target.value;
        setSelectedCompanyId(val ? Number(val) : undefined);
        setSelectedEstateId(undefined);
        setSelectedDivisionId(undefined);
    };

    const handleEstateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val = e.target.value;
        setSelectedEstateId(val ? Number(val) : undefined);
        setSelectedDivisionId(undefined);
    };

    const handleDivisionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val = e.target.value;
        setSelectedDivisionId(val ? Number(val) : undefined);
    };

    return (
        <Box
            bg="white"
            p={{ base: 4, md: 6 }}
            borderRadius="xl"
            boxShadow="sm"
            border="1px"
            borderColor="gray.100"
        >
            <VStack spacing={4} align="stretch">
                <Grid
                    templateColumns={{
                        base: '1fr',
                        md: 'repeat(2, 1fr)',
                        lg: `repeat(${4 + (isDateRange ? 1 : 0) + (showActivityFilter ? 1 : 0) + (showGroupBy ? 1 : 0)}, 1fr)`,
                    }}
                    gap={4}
                >
                    <FormControl>
                        <FormLabel fontSize="sm" color="gray.600" mb={1}>
                            {isDateRange ? 'Tanggal Mulai' : 'Tanggal'}
                        </FormLabel>
                        <Input
                            {...inputStyles}
                            type="date"
                            value={currentDate}
                            onChange={(e) => {
                                setCurrentDate(e.target.value);
                                if (e.target.value) setEndDate((prev) => clampEndDate(e.target.value, prev));
                            }}
                        />
                    </FormControl>

                    {isDateRange && (
                        <FormControl>
                            <FormLabel fontSize="sm" color="gray.600" mb={1}>
                                Tanggal Akhir
                            </FormLabel>
                            <Input
                                {...inputStyles}
                                type="date"
                                value={endDate}
                                min={currentDate}
                                max={dayjs(currentDate).add(MAX_RANGE_DAYS - 1, 'day').format('YYYY-MM-DD')}
                                onChange={(e) => {
                                    if (e.target.value) setEndDate(clampEndDate(currentDate, e.target.value));
                                }}
                            />
                        </FormControl>
                    )}

                    <FormControl>
                        <FormLabel fontSize="sm" color="gray.600" mb={1}>
                            Company
                        </FormLabel>
                        <Select
                            {...inputStyles}
                            value={selectedCompanyId || ''}
                            onChange={handleCompanyChange}
                        >
                            {companies.map((company) => (
                                <option key={company.id} value={company.id}>
                                    {company.name}
                                </option>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl>
                        <FormLabel fontSize="sm" color="gray.600" mb={1}>
                            Estate
                        </FormLabel>
                        <Select
                            {...inputStyles}
                            placeholder={isDivisionRequired ? undefined : "Semua Estate"}
                            value={selectedEstateId || ''}
                            onChange={handleEstateChange}
                        >
                            {filteredEstates.map((estate) => (
                                <option key={estate.id} value={estate.odoo_id}>
                                    {estate.name}
                                </option>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl>
                        <FormLabel fontSize="sm" color="gray.600" mb={1}>
                            Divisi
                        </FormLabel>
                        <Select
                            {...inputStyles}
                            placeholder={isDivisionRequired ? "Pilih Divisi" : "Semua Divisi"}
                            value={selectedDivisionId || ''}
                            onChange={handleDivisionChange}
                            isDisabled={!selectedEstateId || divisions.length === 0}
                        >
                            {divisions.map((division) => (
                                <option key={division.id} value={division.odoo_id}>
                                    {division.name}
                                </option>
                            ))}
                        </Select>
                    </FormControl>

                     {showActivityFilter && (
                        <FormControl>
                            <FormLabel fontSize="sm" color="gray.600" mb={1}>
                                Jenis Aktivitas
                            </FormLabel>
                            <Select
                                {...inputStyles}
                                value={activityCode}
                                onChange={(e) => setActivityCode(e.target.value)}
                            >
                                <option value="all">Semua Aktivitas</option>
                                <option value="8108112">Muat TBS</option>
                                <option value="8108109">Langsir TBS</option>
                            </Select>
                        </FormControl>
                    )}

                    {showGroupBy && (
                        <FormControl>
                            <FormLabel fontSize="sm" color="gray.600" mb={1}>
                                Kelompokkan Berdasarkan
                            </FormLabel>
                            <Select
                                {...inputStyles}
                                value={tempViewMode}
                                onChange={(e) => setTempViewMode(e.target.value as 'block' | 'spb')}
                            >
                                <option value="block">BCC</option>
                                <option value="spb">SPB</option>
                            </Select>
                        </FormControl>
                    )}
                </Grid>

                <Flex
                    gap={2}
                    justify={{ base: 'stretch', md: 'flex-end' }}
                    direction={{ base: 'column', sm: 'row' }}
                >
                    {onDownloadExcel && (
                        <Tooltip
                            label={isExcelDisabled ? 'Tidak ada data untuk diexport' : 'Export ke Excel'}
                            hasArrow
                        >
                            <Box display="inline-block" flex={{ base: 1, sm: 'initial' }}>
                                <Button
                                    rightIcon={<FiDownload />}
                                    size="sm"
                                    variant="ghost"
                                    onClick={onDownloadExcel}
                                    isDisabled={isExcelDisabled || isLoading}
                                    colorScheme="green"
                                    border="1px solid"
                                    borderColor="green.200"
                                    _hover={isExcelDisabled || isLoading ? undefined : {
                                        borderColor: "green.500",
                                        bg: "green.50"
                                    }}
                                    borderRadius="lg"
                                    w="full"
                                >
                                    Excel
                                </Button>
                            </Box>
                        </Tooltip>
                    )}
                    {onDownloadPDF && (
                        <Button
                            rightIcon={<FiDownload />}
                            size="sm"
                            variant="ghost"
                            onClick={onDownloadPDF}
                            isDisabled={isDownloadDisabled || isLoading}
                            colorScheme="red"
                            border="1px solid"
                            borderColor="red.200"
                            _hover={isDownloadDisabled || isLoading ? undefined : {
                                borderColor: "red.500",
                                bg: "red.50"
                            }}
                            borderRadius="lg"
                            flex={{ base: 1, sm: 'initial' }}
                        >
                            PDF
                        </Button>
                    )}
                    <Button
                        leftIcon={<MdClear />}
                        onClick={handleReset}
                        isDisabled={isLoading}
                        size="sm"
                        variant="outline"
                        colorScheme="red"
                        borderRadius="lg"
                        flex={{ base: 1, sm: 'initial' }}
                    >
                        Reset
                    </Button>
                    <Button
                        leftIcon={<FiSearch />}
                        colorScheme="blue"
                        onClick={handleApplyFilters}
                        isLoading={isLoading}
                        isDisabled={isDivisionRequired && !selectedDivisionId}
                        size="sm"
                        borderRadius="lg"
                        flex={{ base: 1, sm: 'initial' }}
                    >
                        Terapkan Filter
                    </Button>
                </Flex>
            </VStack>
        </Box>
    );
};
